import type {
  CourseCompletionEntry,
  DepartmentPerformance,
  UserReportEntry,
  LearningEngagement,
  ProgressDistribution,
  LearningHoursData,
  LoginActivityEntry,
  UserGrowthEntry,
  CertificationDistribution,
  RoleDistributionEntry,
  ActivityHeatmapEntry,
} from '@/services/PagesServices/AnalyticsService';

export const safeNum = (val: unknown): number => {
  const n = Number(val);
  return isNaN(n) || !isFinite(n) ? 0 : n;
};

export interface ComputedInsights {
  avgCompletionRate: number;
  topDept: DepartmentPerformance | null;
  totalCerts: number;
  enrollmentRate: number;
}

export function computeInsights(
  courseCompletion: CourseCompletionEntry[],
  deptPerformance: DepartmentPerformance[],
  userReport: UserReportEntry[],
  engagement: LearningEngagement | null
): ComputedInsights {
  const avgCompletionRate = courseCompletion.length > 0
    ? Math.round(courseCompletion.reduce((sum, c) => sum + safeNum(c.completionRate), 0) / courseCompletion.length)
    : 0;
  const topDept = deptPerformance.length > 0
    ? [...deptPerformance].sort((a, b) => safeNum(b.completionPercentage) - safeNum(a.completionPercentage))[0]
    : null;
  const totalCerts = userReport.reduce((sum, u) => sum + safeNum(u.certificatesEarned), 0);
  const enrollmentRate = engagement
    ? Math.round((safeNum(engagement.usersWithEnrollments) / Math.max(safeNum(engagement.totalActiveUsers), 1)) * 100)
    : 0;
  return { avgCompletionRate, topDept, totalCerts, enrollmentRate };
}

export function computeHealthScore(
  insights: ComputedInsights,
  engagement: LearningEngagement | null,
  deptPerformance: DepartmentPerformance[]
): number {
  let score = 0;
  let factors = 0;
  if (insights.avgCompletionRate > 0) { score += Math.min(insights.avgCompletionRate, 100); factors++; }
  if (insights.enrollmentRate > 0) { score += Math.min(insights.enrollmentRate, 100); factors++; }
  if (engagement && safeNum(engagement.totalActiveUsers) > 0) {
    const activeRatio = Math.min((safeNum(engagement.activeUsersLast7Days) / safeNum(engagement.totalActiveUsers)) * 100, 100);
    score += activeRatio;
    factors++;
  }
  if (deptPerformance.length > 0) {
    const avgDeptCompletion = deptPerformance.reduce((s, d) => s + safeNum(d.completionPercentage), 0) / deptPerformance.length;
    score += Math.min(avgDeptCompletion, 100);
    factors++;
  }
  return factors > 0 ? Math.round(score / factors) : 0;
}

export function computeFunnelData(engagement: LearningEngagement | null, progressDist: ProgressDistribution | null) {
  if (!engagement || !progressDist) return null;
  const totalEnrolled = safeNum(engagement.totalEnrollments);
  const started = totalEnrolled - safeNum(progressDist.notStarted);
  const inProgress = safeNum(progressDist.intermediate) + safeNum(progressDist.advanced) + safeNum(progressDist.completed);
  const completed = safeNum(progressDist.completed);
  return [
    { label: 'Enrolled', value: totalEnrolled, color: '#3b82f6' },
    { label: 'Started', value: started, color: '#8b5cf6' },
    { label: 'In Progress', value: inProgress, color: '#f59e0b' },
    { label: 'Completed', value: completed, color: '#10b981' },
  ];
}

export function computeProgressSegments(progressDist: ProgressDistribution | null) {
  if (!progressDist) return [];
  const ns = safeNum(progressDist.notStarted);
  const bg = safeNum(progressDist.beginner);
  const im = safeNum(progressDist.intermediate);
  const ad = safeNum(progressDist.advanced);
  const cm = safeNum(progressDist.completed);
  const total = ns + bg + im + ad + cm;
  if (total === 0) return [];
  return [
    { label: 'Not Started', value: ns, pct: Math.round((ns / total) * 100), color: '#e5e7eb' },
    { label: '1-25%', value: bg, pct: Math.round((bg / total) * 100), color: '#fbbf24' },
    { label: '26-50%', value: im, pct: Math.round((im / total) * 100), color: '#f97316' },
    { label: '51-99%', value: ad, pct: Math.round((ad / total) * 100), color: '#3b82f6' },
    { label: 'Completed', value: cm, pct: Math.round((cm / total) * 100), color: '#10b981' },
  ];
}

export interface LineChartPoint {
  x: number;
  y: number;
  value: number;
  label: string;
}

export interface LineChartData {
  points: LineChartPoint[];
  pathD: string;
  areaD: string;
  yLabels: { value: number; y: number }[];
  width: number;
  height: number;
  padding: number;
  chartW: number;
  chartH: number;
}

export function computeLineChart(dataPoints: { value: number; label: string }[], svgWidth = 560, svgHeight = 200): LineChartData | null {
  if (dataPoints.length === 0) return null;
  const maxVal = Math.max(...dataPoints.map(d => d.value), 1);
  const padding = 30;
  const chartW = svgWidth - padding * 2;
  const chartH = svgHeight - padding * 2;

  const points = dataPoints.map((d, i) => {
    const x = padding + (i / Math.max(dataPoints.length - 1, 1)) * chartW;
    const y = padding + chartH - (d.value / maxVal) * chartH;
    return { x, y, value: d.value, label: d.label };
  });

  const pathD = points.map((p, i) => `${i === 0 ? 'M' : 'L'} ${p.x},${p.y}`).join(' ');
  const areaD = `${pathD} L ${points[points.length - 1].x},${padding + chartH} L ${points[0].x},${padding + chartH} Z`;

  const ySteps = 4;
  const yLabels = Array.from({ length: ySteps + 1 }, (_, i) => ({
    value: Math.round((maxVal / ySteps) * (ySteps - i)),
    y: padding + (i / ySteps) * chartH,
  }));

  return { points, pathD, areaD, yLabels, width: svgWidth, height: svgHeight, padding, chartW, chartH };
}

export function computeBarChart(data: { value: number; label: string }[], svgWidth = 560, svgHeight = 200) {
  if (data.length === 0) return null;
  const maxVal = Math.max(...data.map(d => d.value), 1);
  const padding = 30;
  const chartW = svgWidth - padding * 2;
  const chartH = svgHeight - padding * 2;
  const barWidth = chartW / data.length * 0.6;

  const bars = data.map((d, i) => {
    const x = padding + (i / data.length) * chartW + (chartW / data.length * 0.2);
    const barH = (d.value / maxVal) * chartH;
    const y = padding + chartH - barH;
    return { x, y, width: barWidth, height: barH, ...d };
  });

  return { bars, width: svgWidth, height: svgHeight, padding, chartW, chartH, maxVal };
}

export function computeDonutData<T extends { percentage: number }>(data: T[]) {
  if (data.length === 0) return [];
  let cumulative = 0;
  return data.map(item => {
    const start = cumulative;
    cumulative += item.percentage;
    return { ...item, startAngle: (start / 100) * 360, endAngle: (cumulative / 100) * 360 };
  });
}

export function computeRoleDonut(roleDistribution: RoleDistributionEntry[]) {
  if (roleDistribution.length === 0) return [];
  const total = roleDistribution.reduce((s, r) => s + r.count, 0);
  let cumulative = 0;
  return roleDistribution.map(role => {
    const pct = total > 0 ? (role.count / total) * 100 : 0;
    const start = cumulative;
    cumulative += pct;
    return { ...role, percentage: Math.round(pct), startAngle: (start / 100) * 360, endAngle: (cumulative / 100) * 360 };
  });
}

export function computeHeatmapGrid(heatmapData: ActivityHeatmapEntry[]) {
  if (heatmapData.length === 0) return null;
  const maxCount = Math.max(...heatmapData.map(d => d.count), 1);
  const days = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];
  const hours = [6, 8, 10, 12, 14, 16, 18, 20, 22];
  const grid: { day: string; hour: number; count: number; intensity: number }[][] = [];

  days.forEach(day => {
    const row: { day: string; hour: number; count: number; intensity: number }[] = [];
    hours.forEach(hour => {
      const entry = heatmapData.find(e => e.day === day && e.hour === hour);
      const count = entry?.count ?? 0;
      row.push({ day, hour, count, intensity: count / maxCount });
    });
    grid.push(row);
  });

  return { grid, days, hours, maxCount };
}

export function describeArc(cx: number, cy: number, r: number, startAngle: number, endAngle: number): string {
  const rad = (deg: number) => (deg - 90) * Math.PI / 180;
  const start = { x: cx + r * Math.cos(rad(endAngle)), y: cy + r * Math.sin(rad(endAngle)) };
  const end = { x: cx + r * Math.cos(rad(startAngle)), y: cy + r * Math.sin(rad(startAngle)) };
  const largeArc = endAngle - startAngle > 180 ? 1 : 0;
  return `M ${start.x} ${start.y} A ${r} ${r} 0 ${largeArc} 0 ${end.x} ${end.y}`;
}

export function filterAndPaginate<T>(
  items: T[],
  search: string,
  searchFields: (keyof T)[],
  page: number,
  perPage: number
): { filtered: T[]; paginated: T[]; totalPages: number } {
  const filtered = search
    ? items.filter(item => searchFields.some(field => String(item[field] ?? '').toLowerCase().includes(search.toLowerCase())))
    : items;
  const totalPages = Math.ceil(filtered.length / perPage);
  const start = (page - 1) * perPage;
  const paginated = filtered.slice(start, start + perPage);
  return { filtered, paginated, totalPages };
}
